import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Container, Header, Subtitle, Section, SectionTitle, SectionBody, Label, ReadOnly, ActionBar, SaveButton, HelpText, DeleteLocationButton, TextButton, DeleteActionsContainer, HeaderRow, BackButton, PageTitle } from './LocationPage.styles'
import { useHistory } from 'react-router'
import { MenuItem } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DefaultButton from 'components/Buttons/DefaultButton'
import FixedLoader from 'components/FixedLoader/FixedLoader'
import FixedError from 'components/FixedError/FixedError'
import DentistNavbar from 'components/DentistNavbar'
import MaterialInput from 'components/Inputs/MaterialInput/MaterialInput'
import useActionApi from 'hooks/useActionApi'
import usePersistedState from 'hooks/usePersistedState'
import entities from 'reduxStore/entityKeys'
import Api from 'api'
import { getSoftwareOptions } from 'utils/dentalSoftware'
import { Autocomplete } from 'utils/autocomplete'
import { LOCATION_IS_CENTRAL_BOOKED } from 'utils/constants'
import { DeleteLocationModal } from './components'
import { buildLocationUpdatePayload } from './buildLocationUpdatePayload'

type Location = {
  id: number
  email: string
  secondaryEmail?: string | null
  accountingEmail?: string | null
  phone?: string | null
  primaryContact?: string | null
  practiceName: string
  businessOwner: string
  mailingAddress?: string | null
  address: string
  city: string
  province: string
  postalCode: string
  country: number // 0=CA,1=US
  parking?: string | null
  radiography?: string | null
  ultrasonic?: string | null
  avgApptTime?: string | null
  charting?: string | null
  software?: string | null
  scentFreePolicy?: string | null
  isCentralBooked?: number
  isCorporatePaymentOwner?: number
}

type FieldErrors = Partial<Record<keyof Location, string>>

type SelectOption = string | { value: string; label: string }

function isEmailValid(value?: string | null) {
  if (!value) return true
  const re = /[^\s@]+@[^\s@]+\.[^\s@]+/
  return re.test(value)
}

function formatPhoneDisplay(v?: string | null) {
  const digits = (v || '').replace(/\D/g, '')
  if (digits.length === 10) {
    return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`
  }
  return v || ''
}

function normalizePhone(v?: string | null) {
  if (!v) return ''
  let d = v.replace(/\D/g, '')
  if (d.length > 10 && d[0] === '1') d = d.slice(1)
  return d
}

function normalizeParkingValue(value?: string | null) {
  return value === 'paid-on-site' ? 'temp-pays' : value
}

export default function LocationPage() {
  const history = useHistory()
  const { locationId } = (history.location.state as any) || {}
  const [user] = usePersistedState<any>({ entityName: entities.user })

  const [location, setLocation] = useState<Location | undefined>(undefined)

  const [fetchLocationLoading, fetchLocationError, fetchLocation] = useActionApi<number, Location>({
    apiMethod: Api.getLocationById,
    onSuccess: (res: Location) => {
      const normalizedParking = normalizeParkingValue(res.parking)
      const normalizedLocation = { ...res, parking: normalizedParking }
      setLocation(normalizedLocation)
      setForm(normalizedLocation)
    }
  })

  const [updateLocationLoading, updateLocationError, updateLocation] = useActionApi<{ locationId: number, data: Partial<Location> }, any>({
    apiMethod: Api.updateLocation,
  })

  const [deleteLocationLoading, deleteLocationError, deleteLocationApi] = useActionApi<number, any>({
    apiMethod: Api.deleteLocation,
    onSuccess: () => {
      ;(window as any)?.TempStars?.Dentist?.updateLocationCounter && (window as any).TempStars.Dentist.updateLocationCounter()
      history.push({ pathname: 'dentistHome' })
    },
  })

  const country = location?.country ?? user?.dentist?.country ?? 0
  const softwareOptions = useMemo(() => getSoftwareOptions(country), [country])
  const parkingOptions: SelectOption[] = useMemo(() => ([
    { value: 'free-on-site', label: 'Free on-site' },
    { value: 'temp-pays', label: 'Temp pays for parking' },
    { value: 'street', label: 'Street' },
    { value: 'public-lot', label: 'Pay Parking @ Public Lot' },
    { value: 'no-parking', label: 'No parking available' },
  ]), [])

  const [form, setForm] = useState<Partial<Location>>({})
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [saved, setSaved] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const addressInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (locationId) fetchLocation(locationId)
  }, [locationId])

  useEffect(() => {
    if (location) setForm(prev => ({ ...location, parking: normalizeParkingValue(location.parking) }))
  }, [location])

  useEffect(() => {
    if (!addressInputRef.current || !location) return
    Autocomplete.init({ country: location.country }, ({ state, city, postalCode, address }: any) => {
      setForm(prev => ({ ...prev, address, city, province: state, postalCode }))
    })
    return () => {
      try { Autocomplete.removeListener() } catch {}
    }
  }, [location])

  const setField = useCallback((key: keyof Location, value: any) => {
    setForm(prev => ({ ...prev, [key]: value }))
    setSaved(false)
  }, [])

  const validate = useCallback((): boolean => {
    const errors: FieldErrors = {}
    const required: (keyof Location)[] = ['practiceName','businessOwner','address','city','province','postalCode','phone','primaryContact','parking','radiography','ultrasonic','avgApptTime','charting','software','scentFreePolicy']
    required.forEach(k => { if (!String((form as any)[k] || '').trim()) errors[k] = 'Required' })
    if (!isEmailValid(form.secondaryEmail)) errors.secondaryEmail = 'Invalid email'
    if (!isEmailValid(form.accountingEmail)) errors.accountingEmail = 'Invalid email'
    if (form.phone && normalizePhone(form.phone).length !== 10) errors.phone = 'Enter 10 digits'
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }, [form])

  const onSave = useCallback(async () => {
    if (!location) return
    if (!validate()) return
    const payload = buildLocationUpdatePayload(form)
    await updateLocation({ locationId: location.id, data: payload })
    await Api.updateToken()
    setSaved(true)
  }, [form, location, updateLocation, validate])

  // HYBRID: Pure React navigation instead of exitReact
  const onBack = useCallback(() => {
    history.push({ pathname: 'dentistLocations' });
  }, [history])

  const is123DNonOwner = useMemo(() =>
    location?.isCentralBooked === LOCATION_IS_CENTRAL_BOOKED._123_DENTIST
    && !location?.isCorporatePaymentOwner,
    [location]
  )

  // HYBRID: Pure React navigation instead of exitReact
  const goUpdatePayment = useCallback(() => {
    if (!location) return
    history.push({ pathname: 'paymentInfo', state: { locationId: location.id, paymentPurpose: 'revenue' } });
  }, [history, location])

  const goDeleteAccount = useCallback(() => {
    const userEmail = user?.locations?.[0]?.practiceName || location?.practiceName
    history.push('dentistDeleteAccount', { userEmail })
  }, [history, location, user])

  const openDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(true)
  }, [])

  const closeDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false)
  }, [])

  const confirmDeleteLocation = useCallback(async () => {
    if (!location) return
    await deleteLocationApi(location.id)
  }, [location, deleteLocationApi])

  const field = (label: string, key: keyof Location, options?: { select?: SelectOption[], readOnly?: boolean, inputProps?: any, id?: string, helpText?: string }) => (
    <>
      <Label>{label}</Label>
      {options?.select ? (
        <MaterialInput select label={label} name={String(key)} value={(form as any)[key] || ''} onChange={(e: any) => setField(key, e.target.value)} error={!!fieldErrors[key]} helperText={(fieldErrors[key] as any) || options?.helpText || ' '}>
          {options.select.map(option => {
            const value = typeof option === 'string' ? option : option.value
            const display = typeof option === 'string' ? option : option.label
            return <MenuItem key={value} value={value}>{display}</MenuItem>
          })}
        </MaterialInput>
      ) : (
        <MaterialInput
          id={options?.id}
          label={label}
          name={String(key)}
          value={(form as any)[key] ?? ''}
          onChange={(e: any) => setField(key, e.target.value)}
          error={!!fieldErrors[key]}
          helperText={(fieldErrors[key] as any) || options?.helpText || ' '}
          inputRef={key==='address' ? addressInputRef : undefined}
          inputProps={options?.inputProps}
          disabled={options?.readOnly}
          shrink
        />
      )}
    </>
  )

  // Get user info for navbar
  const currentUser = (window as any)?.TempStars?.User?.getCurrentUser?.();
  const dentist = currentUser?.dentist;
  const navFirstName = dentist?.firstName || user?.dentist?.firstName || '';
  const navLastName = dentist?.lastName || user?.dentist?.lastName || '';

  return (
    <>
      <DentistNavbar
        firstName={navFirstName}
        lastName={navLastName}
        showGetTempButton={false}
      />
      <Container>
        <HeaderRow>
          <BackButton 
            className="no-fastclick" 
            onClick={onBack}
            aria-label="Back"
          >
            <ArrowBackIcon />
            <span>Back</span>
          </BackButton>
          <PageTitle>Office Location</PageTitle>
        </HeaderRow>
        <Header>
          <Subtitle>Change your location information here</Subtitle>
          <ActionBar>
            <SaveButton
              className="no-fastclick"
              variant="contained"
              size="small"
              onClick={onSave}
              disabled={updateLocationLoading}
            >
              {updateLocationLoading ? 'Saving…' : saved ? 'Saved!' : 'Save Changes'}
            </SaveButton>
            <DefaultButton
              className="no-fastclick"
              variant="contained"
              size="small"
              color="primary"
              onClick={goUpdatePayment}
            >
              {is123DNonOwner ? 'View Payment Info' : 'Update Payment Info'}
            </DefaultButton>
          </ActionBar>
        </Header>

        {location && (
          <>
            <Section>
              <SectionTitle>Contact Information</SectionTitle>
              <SectionBody>
                {field('Email','email', { readOnly: true, helpText: 'This is your login email and notifications are sent here' })}
                {field('Secondary Email','secondaryEmail', { helpText: 'Receive notifications to this email as well' })}
                {field('Accounting Dept. Email','accountingEmail', { helpText: 'Accounting department who needs copies of invoices' })}
                {field('Office Phone','phone', { inputProps: { inputMode: 'tel' } })}
                {field('Primary Contact','primaryContact')}
              </SectionBody>
            </Section>

            <Section>
              <SectionTitle>Practice Details</SectionTitle>
              <SectionBody>
                {field('Practice Name','practiceName')}
                {field('Business Owner','businessOwner')}
              </SectionBody>
            </Section>

            <Section>
              <SectionTitle>Address Information</SectionTitle>
              <SectionBody>
                {field('Mailing Address','address', { id: 'autocomplete' })}
                {field('City','city')}
                {field(country === 0 ? 'Province' : 'State','province')}
                {field(country === 0 ? 'Postal Code' : 'Zip Code','postalCode')}
              </SectionBody>
            </Section>

            <Section>
              <SectionTitle>Office Features & Equipment</SectionTitle>
              <SectionBody>
                {field('Parking','parking', { select: parkingOptions })}
                {field('Radiography','radiography', { select: ['film','digital','phosphor-plates'] })}
                {field('Ultrasonic','ultrasonic', { select: ['cavitron','piezo','both','none'] })}
                {field('Avg Recall','avgApptTime', { select: ['30min','40min','45min','50min','60min','70min'] })}
                {field('Charting','charting', { select: ['paper charts','digital charts'] })}
                {field('Software','software', { select: softwareOptions })}
              {field('Scent-Free Policy','scentFreePolicy', { select: ['scent-free','not-scent-free'] })}
              </SectionBody>
            </Section>

            <DeleteActionsContainer>
              <DeleteLocationButton
                className="no-fastclick"
                variant="contained"
                size="medium"
                onClick={openDeleteModal}
                disabled={deleteLocationLoading}
              >
                Delete This Location
              </DeleteLocationButton>
              <TextButton
                className="no-fastclick"
                onClick={goDeleteAccount}
                disabled={deleteLocationLoading}
              >
                Delete My Entire Account
              </TextButton>
            </DeleteActionsContainer>
          </>
        )}
         <FixedLoader loading={fetchLocationLoading || updateLocationLoading || deleteLocationLoading} />
         <FixedError error={fetchLocationError || updateLocationError || deleteLocationError} />

         <DeleteLocationModal
           isOpen={isDeleteModalOpen}
           onClose={closeDeleteModal}
           onConfirm={confirmDeleteLocation}
           locationName={location?.practiceName || ''}
           isDeleting={deleteLocationLoading}
         />
      </Container>
      </>
  )
}
